import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { useSEO } from '@/hooks/useSEO';
import { articles, categories } from '@/mocks/articles';

const categoryTabs = [
  { label: '漫画', slug: 'manhua' },
  { label: '小说', slug: 'xiaoshuo' },
  { label: '画集', slug: 'huaji' },
  { label: 'CP同人', slug: 'cp' },
  { label: '活动', slug: 'huodong' },
];

const categoryDescriptions: Record<string, string> = {
  manhua: '精选火影忍者同人漫画作品，涵盖短篇、连载、四格等多种形式，展现粉丝们对忍者世界的无限想象。火影同人漫画是火影同人本子中最受欢迎的创作形式之一。',
  xiaoshuo: '火影忍者同人小说创作合集，包含长篇连载、短篇故事、AU设定等丰富体裁。火影同人小说为读者带来全新的忍者世界体验。',
  huaji: '高质量火影同人插画、CG集、设定集，汇集国内外优秀画师的火影二次创作作品。火影同人画集展现粉丝的艺术才华。',
  cp: '火影忍者CP向同人创作专区，涵盖佐鸣、卡伊、带卡等热门CP的漫画与小说作品。火影CP同人是火影同人创作中最具话题性的领域。',
  huodong: '国内外火影同人展会、线上活动、合志企划等最新资讯与参与指南。火影同人活动连接线上线下的火影同人爱好者。',
};

const categoryKeywords: Record<string, string> = {
  manhua: '火影同人漫画,火影同人本子,火影忍者同人,同人漫画推荐,火影同人创作',
  xiaoshuo: '火影同人小说,火影同人本子,火影忍者同人,同人小说推荐,火影同人创作',
  huaji: '火影同人画集,火影同人本子,火影忍者同人,同人插画,火影画集',
  cp: '火影CP同人,火影同人本子,佐鸣同人,带卡同人,火影同人创作',
  huodong: '火影同人活动,火影同人本子,同人展会,火影同人祭,火影同人创作',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'manhua');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const categoryName = currentCategory?.name || '分类';
  const categoryDesc = categoryDescriptions[activeSlug] || currentCategory?.description || '';
  const categoryKw = categoryKeywords[activeSlug] || '火影同人本子,火影忍者同人,火影同人创作';
  const canonical = `https://huoying-tongren.com/category/${activeSlug}`;

  useSEO(
    `${categoryName} | 火影同人本子·精选创作合集`,
    categoryDesc,
    categoryKw,
    canonical,
  );

  const categoryJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categoryName,
      description: categoryDesc,
      url: canonical,
      inLanguage: 'zh-CN',
      isPartOf: {
        '@type': 'WebSite',
        name: '火影同人本子·精选创作合集',
        url: 'https://huoying-tongren.com/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://huoying-tongren.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: categoryName,
          item: canonical,
        },
      ],
    },
  ];

  return (
    <>
      <ScrollToTop />
      <JsonLd data={categoryJsonLd} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryName },
          ]}
        />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                {currentCategory?.description || ''}
              </p>
            </div>

            <div className="flex items-center justify-center mb-10">
              <div className="inline-flex items-center bg-background-100 rounded-full px-1 py-1 flex-wrap justify-center gap-1">
                {categoryTabs.map((tab) => (
                  <Link
                    key={tab.slug}
                    to={`/category/${tab.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSlug === tab.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </Link>
                ))}
              </div>
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:ring-2 hover:ring-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={`${article.title} - 火影同人本子`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}